import React, { useLayoutEffect, useState } from 'react';
import { Typography, Grid, Box, TextField, Autocomplete, IconButton, Divider } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import globalStyles from '../../../assets/styles/global.module.scss';
import styles from '../../../assets/styles/componentStyles/templateLibraryStyles/templateFilters.module.scss';
import popupStyles from '../../../assets/styles/componentStyles/globalPopupStyles.module.scss';

import GlobalPopUp from '../../utility/globalPopUp';
import GlobalSearch from '../../utility/GlobalSearch/GlobalSearch';
import { useTemplateLibraryStore } from '../../../store/templateLibraryStore';
import { useCarouselFunctions } from './utils/useCarouselFunctions';


const renderFilterOption = (
  title: string,
  options: string[],
  value: string,
  onChange: (value: string) => void,
  placeholder: string
) => {
  return (
    <Box className={styles.templateFilters__filtersBox}>
      <Typography className={styles.templateFilters__title}>
        {title}
      </Typography>

      <Autocomplete
        options={options}
        value={value}
        onChange={(_, value) => onChange(value ?? "All")}
        size="small"
        disableClearable
        fullWidth
        getOptionLabel={(option) =>
          option != null && typeof option === "string" ? option : ""
        }
        className={styles.templateFilters__filtersAutocomplete}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            className={styles.templateFilters__listItem}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            className={`${styles.templateFilters__listItem} ${
              selected ? styles.templateFilters__listItemSelected : ""
            }`.trim()}
          >
            {option}
          </li>
        )}
      />
    </Box>
  );
};


const TemplateFilters = () => {

  const { isScreenBelow1250 } = useCarouselFunctions();

  const filters = useTemplateLibraryStore((state) => state.filters);
  const setFilters = useTemplateLibraryStore((state) => state.setFilters);

  const getTemplateLibraryUseCaseListApiCall = useTemplateLibraryStore((state) => state.getTemplateLibraryUseCaseListApiCall);
  const getTemplateLibraryCategoryListApiCall = useTemplateLibraryStore((state) => state.getTemplateLibraryCategoryListApiCall);
  const getTemplateLibraryIndustryListApiCall = useTemplateLibraryStore((state) => state.getTemplateLibraryIndustryListApiCall);

  const templateLibraryUseCaseList = useTemplateLibraryStore((state) => state.templateLibraryUseCaseList);
  const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
  const templateLibraryIndustryList = useTemplateLibraryStore((state) => state.templateLibraryIndustryList);

  const [openFilterPopup, setOpenFilterPopup] = useState(false);

  useLayoutEffect(() => {
    if (!templateLibraryUseCaseList) {
      getTemplateLibraryUseCaseListApiCall();
    }
    if (!templateLibraryCategoryList) {
      getTemplateLibraryCategoryListApiCall();
    }
    if (!templateLibraryIndustryList) {
      getTemplateLibraryIndustryListApiCall();
    }
  }, []);

  const useCaseFilters = ['All', ...(templateLibraryUseCaseList?.map((useCase) => useCase?.template_library_use_case_name).filter(Boolean) || [])];
  const categoryFilters = ['All', ...(templateLibraryCategoryList?.map((category) => category?.whatsapp_template_category_name).filter(Boolean) || [])];
  const industryFilters = ['All', ...(templateLibraryIndustryList?.map((industry) => industry?.template_library_industry_name).filter(Boolean) || [])];

  const renderFilterOptions = (
    <>
      {categoryFilters.length > 0 && (
        renderFilterOption('Category', categoryFilters, (filters?.category || 'All'), (value) => setFilters({ category: value ?? 'All' }), 'Categories')
      )}
      
      {useCaseFilters.length > 0 && (
        renderFilterOption('Use Case', useCaseFilters, (filters?.useCase || 'All'), (value) => setFilters({ useCase: value ?? 'All' }), 'Use Cases')
      )}

      {industryFilters.length > 0 && (
        renderFilterOption('Industry', industryFilters, (filters?.industry || 'All'), (value) => setFilters({ industry: value ?? 'All' }), 'Industries')
      )}
    </>
  );

  return (
    <>
      <Grid container>
        <Grid item className={`${styles.templateFilters} ${isScreenBelow1250 ? styles['templateFilters--compact'] : ''} ${globalStyles.fullwidth}`}>
          
          {isScreenBelow1250 ? (
            <Box className={styles.templateFilters__compactLayout}>
              <Box className={styles.templateFilters__searchBox}>
                <GlobalSearch
                  fullWidth
                  size="small"
                  className={styles.templateFilters__search}
                  value={filters?.search || ''}
                  placeholder="Search templates..."
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilters({ search: e.target.value })
                  }
                />
              </Box>
              <IconButton
                className={styles.templateFilters__filterTrigger}
                onClick={() => setOpenFilterPopup(true)}
                aria-label="Open filters"
              >
                <FilterAltOutlinedIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box className={styles.templateFilters__filtersBox}>
                
                <Typography className={styles.templateFilters__title}>
                  Search
                </Typography>
                
                <GlobalSearch
                  fullWidth
                  size="small"
                  value={filters?.search || ''}
                  placeholder="Search templates..."
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilters({ search: e.target.value })
                  }
                  style={{ marginTop: "5px", marginBottom: "2px" }}
                />

              </Box>

              {renderFilterOptions}
            </>
          )}

        </Grid>
      </Grid>

      <GlobalPopUp
        open={openFilterPopup}
        onClose={() => setOpenFilterPopup(false)}
        handleClose={() => setOpenFilterPopup(false)}
        flagShowCloseIcon 
        titlePadding="0px"
        paperMinWidth="300px"
        paperMaxwidth="340px"
      >
        <Grid container direction="column" justifyContent="center" className={globalStyles.fullwidth}>
          
          <Grid item xs={12} textAlign="center">
            <Typography className={`${popupStyles.popupTitleTypography} ${styles.templateFilters__popupTitle}`}>
              Filters
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider className={`${popupStyles.popupTitleDivider} ${styles.templateFilters__popupDivider}`} />
          </Grid>

          <Grid item xs={12} direction="column"
            className={`${popupStyles.filterPopupBodyContainer} ${styles.templateFilters__popupFields}`}
          >
            {renderFilterOptions}
          </Grid>

        </Grid>

      </GlobalPopUp>

    </>
  );
};

export default TemplateFilters;